import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, HelpCircle, Loader2, Phone } from "lucide-react";
import { fmtDuration, fmtDate } from "@/pages/audit/AuditParts";

export function DrillDialog({ drillStudent, setDrillStudent, setDrillSessions, drillLoading, drillSessions }) {
  return (
    <Dialog open={!!drillStudent} onOpenChange={(o) => { if (!o) { setDrillStudent(null); setDrillSessions([]); } }}>
      <DialogContent className="max-w-3xl" data-testid="audit-drill-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}>
            {drillStudent?.student_name} · Tuition sessions
          </DialogTitle>
          <DialogDescription>
            {drillStudent?.roll_number} · Class {drillStudent?.class_name}-{drillStudent?.section} · {fmtDuration(drillStudent?.total_seconds)} total active time
          </DialogDescription>
        </DialogHeader>
        {drillLoading ? (
          <div className="py-10 flex items-center justify-center text-slate-400 gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading sessions…
          </div>
        ) : drillSessions.length === 0 ? (
          <div className="py-10 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
            <HelpCircle className="h-4 w-4" /> No sessions in the selected range.
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-900/40 text-xs uppercase tracking-wider text-slate-500 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">Subject · Lesson</th>
                  <th className="text-left px-3 py-2">Started</th>
                  <th className="text-right px-3 py-2">Active</th>
                  <th className="text-right px-3 py-2">Qs</th>
                  <th className="text-left px-3 py-2">Ended</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {drillSessions.map((s) => (
                  <tr key={s.id}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-900 dark:text-slate-100">{s.subject_name}</div>
                      <div className="text-xs text-slate-500 truncate">{s.lesson_title}</div>
                    </td>
                    <td className="px-3 py-2 text-slate-600 dark:text-slate-300 text-xs">{fmtDate(s.started_at)}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-600 dark:text-emerald-300">{fmtDuration(s.active_seconds)}</td>
                    <td className="px-3 py-2 text-right font-mono">{s.question_count}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{s.ended_at ? fmtDate(s.ended_at) : <span className="text-amber-500">in progress</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setDrillStudent(null)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function HeatmapDrillDialog({ hmDrill, setHmDrill, setHmSessions, hmDrillLoading, hmSessions, exportHeatSlot }) {
  return (
    <Dialog open={!!hmDrill} onOpenChange={(o) => { if (!o) { setHmDrill(null); setHmSessions([]); } }}>
      <DialogContent className="max-w-3xl" data-testid="audit-heatmap-drill-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}>Sessions · {hmDrill?.label}</DialogTitle>
          <DialogDescription>
            Tutor sessions that started on {hmDrill?.label} (Asia/Kolkata) in the selected range &amp; filters.
          </DialogDescription>
        </DialogHeader>
        {hmDrillLoading ? (
          <div className="py-10 flex items-center justify-center text-slate-400 gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading sessions…
          </div>
        ) : hmSessions.length === 0 ? (
          <div className="py-10 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
            <HelpCircle className="h-4 w-4" /> No sessions in this slot.
          </div>
        ) : (
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 dark:bg-slate-900/40 text-xs uppercase tracking-wider text-slate-500 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2">Student</th>
                  <th className="text-left px-3 py-2">Subject · Lesson</th>
                  <th className="text-left px-3 py-2">Started</th>
                  <th className="text-right px-3 py-2">Active</th>
                  <th className="text-right px-3 py-2">Qs</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {hmSessions.map((s) => (
                  <tr key={s.id} data-testid="heatmap-drill-row">
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-900 dark:text-slate-100">{s.student_name}</div>
                      <div className="text-xs text-slate-500 font-mono">{s.roll_number} · {s.class_name}-{s.section}</div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-900 dark:text-slate-100">{s.subject_name}</div>
                      <div className="text-xs text-slate-500 truncate">{s.lesson_title}</div>
                    </td>
                    <td className="px-3 py-2 text-slate-600 dark:text-slate-300 text-xs">{fmtDate(s.started_at)}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-600 dark:text-emerald-300">{fmtDuration(s.active_seconds)}</td>
                    <td className="px-3 py-2 text-right font-mono">{s.question_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={exportHeatSlot} disabled={!hmSessions.length} className="bg-emerald-500 hover:bg-emerald-600 text-white border-0 disabled:opacity-50" data-testid="heatmap-export-slot-btn">
            <Download className="h-3.5 w-3.5 mr-1" /> Export this slot
          </Button>
          <Button variant="outline" onClick={() => setHmDrill(null)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function CheckinDialog({ checkinTarget, setCheckinTarget, lateNightCounts, checkinMsg, setCheckinMsg, checkinSending, sendCheckin }) {
  return (
    <Dialog open={!!checkinTarget} onOpenChange={(o) => { if (!o) setCheckinTarget(null); }}>
      <DialogContent className="max-w-lg" data-testid="checkin-call-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }} className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-amber-500" /> Wellbeing check-in call
          </DialogTitle>
          <DialogDescription>
            Place a voice call to {checkinTarget?.student_name}'s parent
            {checkinTarget && (lateNightCounts[checkinTarget.student_id] ? ` — flagged for ${lateNightCounts[checkinTarget.student_id]} late-night sessions` : "")}.
            Review or edit the message below before sending.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          value={checkinMsg}
          onChange={(e) => setCheckinMsg(e.target.value)}
          rows={6}
          maxLength={600}
          className="resize-none"
          data-testid="checkin-message-input"
        />
        <div className="text-[11px] text-slate-400 text-right">{checkinMsg.length}/600</div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setCheckinTarget(null)} disabled={checkinSending}>Cancel</Button>
          <Button onClick={sendCheckin} disabled={checkinSending} className="bg-amber-500 hover:bg-amber-600" data-testid="checkin-send-btn">
            {checkinSending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Phone className="h-4 w-4 mr-1" />}
            {checkinSending ? "Queuing…" : "Call parent"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
